import { useI18n } from '../../../i18n.js';
import { Icon } from '../../Icon.js';
import { VoiceChatBar } from '../../VoiceChatBar.js';

interface TwentyOneControlsProps {
  isMyTurn: boolean;
  isPaused: boolean;
  stage: 'betting' | 'playing' | 'banker' | 'finished';
  isBanker: boolean;
  betAmount: number;
  maxBet: number;
  setBetAmount: React.Dispatch<React.SetStateAction<number>>;
  playError: string | null;
  mustPlay: boolean;
  playLabel: string;
  passLabel: string;
  onPass: () => void;
  onPlay: () => void;
}

export function TwentyOneControls({
  isMyTurn,
  isPaused,
  stage,
  isBanker,
  betAmount,
  maxBet,
  setBetAmount,
  playError,
  mustPlay,
  playLabel,
  passLabel,
  onPass,
  onPlay,
}: TwentyOneControlsProps) {
  const { lang } = useI18n();
  const isMn = lang === 'mn';

  return (
    <div className="player-controls">
      <div className="player-controls-secondary">
        <VoiceChatBar compact />
      </div>

      <div className="player-controls-primary">
        {stage === 'betting' ? (
          isBanker ? (
            <div style={{ color: '#fbbf24', fontSize: '0.9em', fontWeight: 600 }}>
              👑 {isMn ? 'Та Банкир: Тоглогчдын бооцоог хүлээж байна...' : 'You are Banker: Waiting for bets...'}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,0.4)', padding: '4px 8px', borderRadius: 12 }}>
                <button
                  type="button"
                  className="icon-button"
                  disabled={!isMyTurn || isPaused || betAmount <= 1}
                  onClick={() => setBetAmount((b) => Math.max(1, b - 1))}
                  style={{ padding: '2px 6px', fontSize: '1rem', fontWeight: 800 }}
                >
                  -
                </button>
                <span style={{ minWidth: 40, textAlign: 'center', fontWeight: 800, color: '#fef08a' }}>
                  🪙 {betAmount}
                </span>
                <button
                  type="button"
                  className="icon-button"
                  disabled={!isMyTurn || isPaused || betAmount >= maxBet}
                  onClick={() => setBetAmount((b) => Math.min(maxBet, b + 1))}
                  style={{ padding: '2px 6px', fontSize: '1rem', fontWeight: 800 }}
                >
                  +
                </button>
              </div>
              {[5, 10, 20].filter((v) => v <= maxBet).map((v) => (
                <button
                  key={`quick-bet-${v}`}
                  type="button"
                  className="icon-button"
                  disabled={!isMyTurn || isPaused}
                  onClick={() => setBetAmount(v)}
                  style={{
                    padding: '4px 8px',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    background: betAmount === v ? 'rgba(251, 191, 36, 0.3)' : 'rgba(255,255,255,0.1)',
                    border: betAmount === v ? '1px solid #fbbf24' : '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 8,
                    color: '#fff',
                  }}
                >
                  {v}
                </button>
              ))}
              {maxBet > 0 && (
                <button
                  type="button"
                  className="icon-button"
                  disabled={!isMyTurn || isPaused}
                  onClick={() => setBetAmount(maxBet)}
                  style={{
                    padding: '4px 8px',
                    fontSize: '0.8rem',
                    fontWeight: 800,
                    background: betAmount === maxBet ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.35), rgba(245, 158, 11, 0.35))' : 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(245, 158, 11, 0.6)',
                    borderRadius: 8,
                    color: '#fde047',
                  }}
                  title={isMn ? `Банк цохих (${maxBet} чип)` : `Smash Bank (${maxBet} chips)`}
                >
                  💥 {isMn ? 'Банк цохих' : 'Smash'} ({maxBet})
                </button>
              )}
              <button
                className="play-action"
                onClick={onPlay}
                disabled={!isMyTurn || !!playError || isPaused}
              >
                <Icon name="check" />
                <span>{isMn ? `Бооцоо (${betAmount})` : `Bet (${betAmount})`}</span>
              </button>
            </div>
          )
        ) : (
          <>
            <button
              className="pass-action"
              onClick={onPass}
              disabled={!isMyTurn || mustPlay || isPaused}
              aria-label={passLabel}
            >
              <Icon name="skip-forward" />
              <span>{passLabel}</span>
            </button>
            <button
              className="play-action"
              onClick={onPlay}
              disabled={!isMyTurn || !!playError || isPaused}
              title={playError ?? playLabel}
            >
              <Icon name="arrow-up" />
              <span>{playLabel}</span>
            </button>
          </>
        )}
      </div>

      <div className="player-controls-spacer" />
    </div>
  );
}
